import React from 'react';

import {assertAndContinue} from 'Asserts';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {numericAccessor, stringAccessor} from 'data-frames/util/AccessorUtils';
import {createAccessor} from 'data-frames/util/createAccessors';
import findAndAssertColumn from 'data-frames/util/findAndAssertColumn';
import {sortDataFrameByAccessors} from 'data-frames/util/sortDataFrame';
import {isLeafTypeNumerical} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {AnalyticsVisualizationSortDirection as SortDirection} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import BarChart, {BAR_CHART_TYPE_VALIDATION_MAP, BarChartProps} from 'ui-library/charting/BarChart';
import {aggregateChartDataFrame} from 'ui-library/charting/statistics/aggregation/aggregateChartDataFrame';

import convertBarChartProps from './prop-converters/convertBarChartProps';

type BarChartParameters = ExtractByTypename<
  AnalyticsVisualization['parameters'],
  'AnalyticsVisualizationBarChartParametersNode'
>;

function aggregateBarChartProps(
  barChartProps: BarChartProps,
  {aggregationType, errorBarType = null}: Pick<BarChartParameters, 'aggregationType' | 'errorBarType'>
): BarChartProps {
  if (aggregationType == null) {
    assertAndContinue(errorBarType == null, 'Expected errorBarType to be null when aggregationType is null', {
      aggregationType,
      errorBarType,
    });
    return barChartProps;
  }

  const {dataFrame, columnsMapping, config, ...remainingBarChartProps} = barChartProps;

  const {dataFrame: aggregatedDataFrame, columnsMapping: aggregatedColumnsMapping} = aggregateChartDataFrame(
    dataFrame,
    {
      partitionColumn: columnsMapping.x,
      ...(columnsMapping.series && {secondaryPartitionColumn: columnsMapping.series}),
      valueColumn: columnsMapping.y,
    },
    aggregationType,
    errorBarType
  );

  return {
    ...remainingBarChartProps,
    dataFrame: aggregatedDataFrame,
    columnsMapping: {
      x: aggregatedColumnsMapping.partitionColumn,
      y: aggregatedColumnsMapping.aggregatedValues,
      ...(aggregatedColumnsMapping.secondaryPartitionColumn && {
        series: aggregatedColumnsMapping.secondaryPartitionColumn,
      }),
      ...(aggregatedColumnsMapping.errorBarValues && {errorValue: aggregatedColumnsMapping.errorBarValues}),
    },
    config: {
      ...config,
      yAxis: {
        ...config.yAxis,
        label: findAndAssertColumn(aggregatedDataFrame, aggregatedColumnsMapping.aggregatedValues)
          .displayName,
      },
    },
  };
}

export interface BarChartViewerProps {
  dataFrame: DataFrame;
  parameters: BarChartParameters;
}

export default function BarChartViewer({dataFrame, parameters}: BarChartViewerProps): JSX.Element {
  const conversionOutput = convertBarChartProps(dataFrame, parameters);

  if (!conversionOutput.valid) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text={conversionOutput.message} />;
  }

  const {dataFrame: aggregatedDataFrame, ...barChartProps} = aggregateBarChartProps(
    conversionOutput.props,
    parameters
  );
  const xAxisColumn = findAndAssertColumn(aggregatedDataFrame, parameters.xAxisColumnName);
  const {accessor: xAccessor, validatedLeafType} = createAccessor(
    aggregatedDataFrame,
    xAxisColumn.systemName,
    BAR_CHART_TYPE_VALIDATION_MAP.x
  );
  const typesafeXAccessor = isLeafTypeNumerical(validatedLeafType)
    ? numericAccessor(xAccessor, validatedLeafType)
    : stringAccessor(xAccessor);
  const sortedAggregatedDataFrame =
    parameters.sortDirection != null
      ? sortDataFrameByAccessors(
          aggregatedDataFrame,
          [typesafeXAccessor],
          parameters.sortDirection === SortDirection.ASCENDING
        )
      : aggregatedDataFrame;

  return <BarChart dataFrame={sortedAggregatedDataFrame} {...barChartProps} />;
}
